import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Scanner } from '@yudiel/react-qr-scanner';
import { 
  ScanLine, 
  User, 
  CheckCircle, 
  XCircle, 
  Camera,
  RefreshCw,
  Clock
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { toast } from 'react-hot-toast';

interface ScannedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePhotoUrl?: string;
  role: string;
  status: string;
}

interface ScanResult {
  success: boolean;
  user?: ScannedUser;
  message: string;
  timestamp: Date;
}

const QRReaderPage: React.FC = () => {
  const { businessId } = useBusiness();
  const [scanning, setScanning] = useState(true);
  const [lastResult, setLastResult] = useState<ScanResult | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanResult[]>([]);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const parseQRCode = (code: string): { businessId: string; userId: string } | null => {
    // Formato esperado: DOJO:{businessId}:{userId}
    const parts = code.split(':');
    if (parts.length === 3 && parts[0] === 'DOJO') {
      return {
        businessId: parts[1],
        userId: parts[2]
      };
    }
    return null;
  };

  const handleScan = async (detectedCodes: any[]) => {
    if (!detectedCodes || detectedCodes.length === 0) return;
    
    const code = detectedCodes[0].rawValue;
    if (!code) return;

    // Evitar escaneos repetidos del mismo código en poco tiempo
    if (lastResult && Date.now() - lastResult.timestamp.getTime() < 3000) {
      return;
    }

    setScanning(false);

    try {
      const parsed = parseQRCode(code);
      
      if (!parsed) {
        const result: ScanResult = {
          success: false,
          message: 'Código QR no válido',
          timestamp: new Date()
        };
        setLastResult(result);
        setScanHistory(prev => [result, ...prev].slice(0, 10));
        toast.error('Código QR no válido');
        return;
      }

      // Verificar que el usuario pertenece al mismo negocio
      if (parsed.businessId !== businessId && parsed.businessId !== 'default') {
        const result: ScanResult = {
          success: false,
          message: 'Usuario de otro negocio',
          timestamp: new Date()
        };
        setLastResult(result);
        setScanHistory(prev => [result, ...prev].slice(0, 10));
        toast.error('Este usuario pertenece a otro negocio');
        return;
      }

      // Buscar el usuario en la base de datos
      const userDoc = await getDoc(doc(db, 'users', parsed.userId));
      
      if (!userDoc.exists()) {
        const result: ScanResult = {
          success: false,
          message: 'Usuario no encontrado',
          timestamp: new Date()
        };
        setLastResult(result);
        setScanHistory(prev => [result, ...prev].slice(0, 10));
        toast.error('Usuario no encontrado');
        return;
      }

      const userData = userDoc.data() as ScannedUser;
      const result: ScanResult = {
        success: true,
        user: { ...userData, id: userDoc.id },
        message: 'Usuario identificado',
        timestamp: new Date()
      };
      
      setLastResult(result);
      setScanHistory(prev => [result, ...prev].slice(0, 10));
      toast.success(`¡${userData.firstName} ${userData.lastName} identificado!`);

    } catch (error) {
      console.error('Error al procesar QR:', error);
      const result: ScanResult = {
        success: false,
        message: 'Error al procesar el código',
        timestamp: new Date()
      };
      setLastResult(result);
      setScanHistory(prev => [result, ...prev].slice(0, 10));
      toast.error('Error al procesar el código');
    }
  };

  const handleError = (error: any) => {
    console.error('Error de cámara:', error);
    setCameraError('No se pudo acceder a la cámara. Asegúrate de dar permisos.');
  };

  const resetScanner = () => {
    setLastResult(null);
    setScanning(true);
    setCameraError(null);
  };

  return (
    <>
      <Helmet>
        <title>Lector QR</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <ScanLine className="w-8 h-8 text-primary-500" />
              Lector de Códigos QR
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Escanea el código QR de los usuarios para identificarlos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Scanner */}
          <Card className="p-0 overflow-hidden">
            <div className="bg-gray-900 relative">
              {scanning ? (
                <div className="aspect-square max-h-[500px] relative">
                  {cameraError ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-white p-6">
                      <Camera className="w-16 h-16 text-gray-500 mb-4" />
                      <p className="text-center text-gray-400 mb-4">{cameraError}</p>
                      <Button onClick={resetScanner} variant="outline">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reintentar
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Scanner
                        onScan={handleScan}
                        onError={handleError}
                        constraints={{
                          facingMode: 'environment'
                        }}
                        styles={{
                          container: {
                            width: '100%',
                            height: '100%'
                          },
                          video: {
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }
                        }}
                      />
                      {/* Overlay con guía de escaneo */}
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-64 h-64 border-2 border-primary-500 rounded-2xl relative">
                            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-primary-500 rounded-tl-xl" />
                            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-primary-500 rounded-tr-xl" />
                            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-primary-500 rounded-bl-xl" />
                            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-primary-500 rounded-br-xl" />
                          </div>
                        </div>
                        <div className="absolute bottom-4 left-0 right-0 text-center">
                          <p className="text-white text-sm bg-black/50 inline-block px-4 py-2 rounded-full">
                            Apunta al código QR del usuario
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="aspect-square max-h-[500px] flex flex-col items-center justify-center bg-gray-800 p-6">
                  {lastResult?.success ? (
                    <>
                      <CheckCircle className="w-20 h-20 text-green-500 mb-4" />
                      {lastResult.user?.profilePhotoUrl ? (
                        <img 
                          src={lastResult.user.profilePhotoUrl} 
                          alt={lastResult.user.firstName}
                          className="w-24 h-24 rounded-full object-cover border-4 border-green-500 mb-4"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center mb-4">
                          <span className="text-4xl font-bold text-white">
                            {lastResult.user?.firstName?.charAt(0)}
                          </span>
                        </div>
                      )}
                      <h3 className="text-2xl font-bold text-white">
                        {lastResult.user?.firstName} {lastResult.user?.lastName}
                      </h3>
                      <p className="text-gray-400 mt-1">{lastResult.user?.email}</p>
                      <span className="mt-3 px-4 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
                        Usuario identificado
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-20 h-20 text-red-500 mb-4" />
                      <h3 className="text-xl font-bold text-white">
                        {lastResult?.message || 'Error'}
                      </h3>
                      <span className="mt-3 px-4 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                        No válido
                      </span>
                    </>
                  )}
                  <Button onClick={resetScanner} className="mt-6">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Escanear otro código
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Historial de escaneos */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Historial de escaneos
            </h3>
            
            {scanHistory.length === 0 ? (
              <div className="text-center py-12">
                <ScanLine className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  Aún no hay escaneos
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Los escaneos recientes aparecerán aquí
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {scanHistory.map((result, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-4 p-4 rounded-xl ${
                      result.success 
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    }`}
                  >
                    {result.success ? (
                      <>
                        {result.user?.profilePhotoUrl ? (
                          <img 
                            src={result.user.profilePhotoUrl} 
                            alt={result.user.firstName}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                            <span className="text-lg font-bold text-white">
                              {result.user?.firstName?.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {result.user?.firstName} {result.user?.lastName}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {result.timestamp.toLocaleTimeString('es-ES')}
                          </p>
                        </div>
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                          <XCircle className="w-6 h-6 text-red-500" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {result.message}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {result.timestamp.toLocaleTimeString('es-ES')}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};

export default QRReaderPage;

